import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import {
    Box,
    IconButton,
    InputAdornment,
    Popover,
    TextField,
    Typography,
} from "@mui/material";
import { ACCENT, SURFACE, TEXT } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import { normalizeGuess, placementKey } from "./tutorCrosswordPlay";

function ClueList({ title, items, selectedKey, foundKeys, onSelect, onOpenEnter }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, mb: 1, color: TEXT.primary }}>{title}</Typography>
            {(items || []).map((item) => {
                const key = placementKey(item);
                const found = foundKeys.has(key);
                const selected = selectedKey === key;
                return (
                    <Box
                        key={key}
                        onClick={() => onSelect(item)}
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            mb: 0.75,
                            px: 1,
                            py: 0.75,
                            minHeight: 44,
                            borderRadius: 1,
                            cursor: "pointer",
                            bgcolor: selected ? ACCENT.soft : "transparent",
                            border: selected ? `1px solid ${ACCENT.main}` : "1px solid transparent",
                        }}
                    >
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                minWidth: 28,
                                borderRadius: "50%",
                                bgcolor: found ? ACCENT.main : "#C4FFE0",
                                color: "#111",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                fontWeight: 700,
                                mt: 0.25,
                            }}
                        >
                            {item.number}
                        </Box>
                        <Typography
                            sx={{
                                flex: 1,
                                color: found ? TEXT.secondary : TEXT.primary,
                                textDecoration: found ? "line-through" : "none",
                                lineHeight: 1.4,
                                pt: 0.35,
                            }}
                        >
                            {item.meaning || "—"}
                        </Typography>
                        {found ? null : (
                            <IconButton
                                size="small"
                                aria-label="Enter word"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onSelect(item);
                                    onOpenEnter(event.currentTarget, item);
                                }}
                                sx={{ minWidth: 44, minHeight: 44, color: ACCENT.light }}
                            >
                                <KeyboardReturnIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

export default function TutorsPlayClues({
    puzzle,
    selectedKey,
    foundKeys,
    onSelect,
    onSubmitGuess,
    onHide,
}) {
    const { t } = usePagesTranslation();
    const [anchor, setAnchor] = useState(null);
    const [entry, setEntry] = useState(null);
    const [value, setValue] = useState("");

    const openEnter = (target, item) => {
        setAnchor(target);
        setEntry(item);
        setValue("");
    };

    const closeEnter = () => {
        setAnchor(null);
        setEntry(null);
        setValue("");
    };

    const submit = () => {
        if (!entry) {
            return;
        }
        onSubmitGuess(entry, value);
        closeEnter();
    };

    const maxLength = entry ? String(entry.word || "").length : 12;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                height: "100%",
                border: `1px solid ${SURFACE.border}`,
                borderRadius: 2,
                bgcolor: SURFACE.panel,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 1.5,
                    py: 1,
                    borderBottom: `1px solid ${SURFACE.border}`,
                }}
            >
                <Typography sx={{ fontWeight: 800, color: TEXT.primary }}>
                    {t("tutors.play-clues", "Word Clues")}
                </Typography>
                <IconButton
                    aria-label={t("tutors.play-hide-clues", "Hide word clues")}
                    onClick={onHide}
                    sx={{ minWidth: 44, minHeight: 44, color: TEXT.secondary }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ overflow: "auto", p: 1.25, flex: 1 }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1fr)" },
                        gap: 2,
                    }}
                >
                    <ClueList
                        title={t("tutors.play-across", "Across")}
                        items={puzzle?.across}
                        selectedKey={selectedKey}
                        foundKeys={foundKeys}
                        onSelect={onSelect}
                        onOpenEnter={openEnter}
                    />
                    <ClueList
                        title={t("tutors.play-down", "Down")}
                        items={puzzle?.down}
                        selectedKey={selectedKey}
                        foundKeys={foundKeys}
                        onSelect={onSelect}
                        onOpenEnter={openEnter}
                    />
                </Box>
            </Box>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={closeEnter}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{ paper: { sx: { p: 1.5, minWidth: 220 } } }}
            >
                <TextField
                    autoFocus
                    size="small"
                    fullWidth
                    value={value}
                    placeholder={t("tutors.play-enter-word", "Enter word")}
                    onChange={(event) => setValue(normalizeGuess(event.target.value).slice(0, maxLength))}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            submit();
                        }
                    }}
                    slotProps={{
                        htmlInput: {
                            maxLength,
                            style: { textAlign: "center", fontWeight: 700, letterSpacing: 1 },
                        },
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton aria-label={t("tutors.play-check", "Check")} onClick={submit} edge="end">
                                        <KeyboardReturnIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Popover>
        </Box>
    );
}
